import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { get } from 'lodash';
import dayjs from 'dayjs';
import { ViewportScroller } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Router } from '@angular/router';
import { combineLatest, from, of, skipWhile, Subject, timer } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CatalogFilterSelection,
  catalogFilterKey,
  hasCatalogFilterOptions,
} from './catalog-filters';
import { ShopCatalogFiltersService } from './shop-catalog-filters.service';
import { debounceTime, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { MarketplaceKeys, ShopKeys } from '@config/keys/shop.keys';
import { ProductKeys } from '@config/keys/product.keys';
import { CategoryKeys } from '@config/keys/category.keys';

import { DateUtils } from '@shared/utils/date/date.utils';
import { DoodCategoryWithProduct } from '@shared/interfaces/category.interface';

import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';

import { DoodShopModel, IShopCatalog, IShopCatalogTimeRule } from '@core/models/shop.model';
import {
  CategoryDisplayType,
  DoodProductModel,
  ICategory,
  IProductLabel,
} from '@core/models/product.model';

import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { NativeService } from '@core/services/native/native.service';
import { ShopAvailableAtService } from '@core/services/shops/shop-available-at.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { ProductCategoryCardsWithNameAtomComponent } from '@shared/atoms/product-category-cards-with-name-atom/product-category-cards-with-name-atom.component';
import { ProductCategoryCardsWithoutNameAtomComponent } from '@shared/atoms/product-category-cards-without-name-atom/product-category-cards-without-name-atom.component';
import { ProductCategoryHorizontalHighlightAtomComponent } from '@shared/atoms/product-category-horizontal-highlight-atom/product-category-horizontal-highlight-atom.component';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { Store } from '@ngrx/store';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import { shopProductListBlockResolveConfig } from '@front-platform/content-builder';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import type { ShopProductListBlockConfig } from '@front-platform/content-builder';
import { SHOP_PRODUCT_LIST_BLOCK_DATA_SOURCE } from './shop-product-list-block-view-model';
import { provideShopProductListBlockDataSource } from './shop-product-list-block-data-source.providers';

export interface ShopProductListBlockContract extends ConfigComponentContract<
  Partial<ShopProductListBlockConfig>,
  ShopProductListBlockConfig
> {}

@Component({
  selector: 'app-shop-product-list-block',
  templateUrl: './shop-product-list-block.component.html',
  standalone: false,
  providers: [...provideShopProductListBlockDataSource()],
})
export class ShopProductListBlockComponent
  implements OnInit, OnDestroy, ShopProductListBlockContract
{
  readonly config = input<Partial<ShopProductListBlockConfig>>({});

  readonly resolvedConfig = computedResolvedConfig(shopProductListBlockResolveConfig, () => ({
    config: this.config(),
  }));

  /** Category bar + filter strips: content-builder color, white when unset. */
  protected readonly categoriesBackgroundColor = computed(() =>
    fromContentBuilderColor(
      this.resolvedConfig().categories_background_color as string | undefined,
      '#FFFFFF',
    ),
  );

  private readonly blockDataSource = inject(SHOP_PRODUCT_LIST_BLOCK_DATA_SOURCE);
  protected readonly catalogFilters = inject(ShopCatalogFiltersService);
  protected readonly catalogFiltersOpen = signal(false);
  protected readonly hasCatalogFilterOptions = hasCatalogFilterOptions;

  private readonly categoryTabsContainer =
    viewChild<ElementRef<HTMLElement>>('categoryTabsContainer');
  private readonly catalogFiltersBar = viewChild<ElementRef<HTMLElement>>('catalogFiltersBar');
  protected readonly catalogFiltersStickyTop = signal(0);

  shop$ = this.shopSelector.selectActive$;
  isShopLoading$ = this.shopSelector.selectIsLoading$;

  isOrderingAllowed = true;
  productKeys = ProductKeys;

  cartProducts$ = this.cartRefiner.selectActiveCartProducts;

  protected readonly activeItemId = signal<string | undefined>(undefined);
  protected readonly scrollSpyOffset = signal(55);

  private readonly modalSelector = inject(ModalStoreSelector);

  protected readonly hasOpenModal = toSignal(
    this.modalSelector.selectModals.pipe(map(modals => modals.length > 0)),
    { initialValue: false },
  );

  categoryKeys = CategoryKeys;
  isNotched = false;
  private destroyed$ = new Subject<boolean>();
  availableAtMessage: string | null = null;
  shouldBeAvailableNow = false;

  private previousScrollPosition = 0;

  isScrollingDown = false;

  warningMessages: string[] = [];

  isBurgerNavigationOpen = false;
  filtereableLabels: IProductLabel[] = [];

  protected readonly vm$ = this.blockDataSource.vm$;

  constructor(
    private readonly router: Router,
    private shopRefiner: ShopStoreRefiner,
    private cartRefiner: CartStoreRefiner,
    private shopSelector: ShopStoreSelector,
    private readonly orderDispatcher: OrderStoreDispatcher,
    protected settingsSelector: SettingsStoreSelector,
    private readonly routerHelper: RouterHelperService,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly viewportScroller: ViewportScroller,
    private readonly translateService: TranslateService,
    private readonly shopAvailableAtService: ShopAvailableAtService,
    private readonly analyticsService: AnalyticsService,
    private readonly store: Store,
  ) {
    effect(onCleanup => {
      const filtersStick = this.catalogFilters.applied().length > 0;
      const tabsEl = this.categoryTabsContainer()?.nativeElement;
      const barEl = this.catalogFiltersBar()?.nativeElement;
      if (!tabsEl) {
        return;
      }

      const updateStickyOffsets = () => {
        const stickyTop = this.resolvedConfig().category_list_top ?? 0;
        const tabsHeight = tabsEl.offsetHeight;
        const barHeight = filtersStick ? (barEl?.offsetHeight ?? 0) : 0;
        const next =
          tabsHeight > 0
            ? stickyTop + tabsHeight + barHeight
            : (this.settingsSelector.isDesktopScreen ? 70 : 55) + barHeight;
        queueMicrotask(() => {
          this.scrollSpyOffset.set(next);
          this.catalogFiltersStickyTop.set(stickyTop + tabsHeight);
        });
      };

      const observer = new ResizeObserver(() => updateStickyOffsets());
      observer.observe(tabsEl);
      if (barEl) {
        observer.observe(barEl);
      }
      updateStickyOffsets();
      onCleanup(() => observer.disconnect());
    });

    const activeShopId = toSignal(
      this.shopSelector.selectActive$.pipe(map(shop => shop?.[ShopKeys.Id])),
      { initialValue: undefined },
    );
    let previousShopId: string | undefined;
    effect(() => {
      const shopId = activeShopId();
      if (previousShopId && shopId && previousShopId !== shopId) {
        this.catalogFilters.clear();
        this.catalogFiltersOpen.set(false);
      }
      if (shopId) {
        previousShopId = shopId;
      }
    });

    effect(() => {
      const enabled = this.resolvedConfig().display_allergen_and_tag_filters === true;
      this.blockDataSource.setCatalogFiltersEnabled(enabled);
      if (!enabled && this.catalogFilters.applied().length > 0) {
        this.catalogFilters.clear();
        this.catalogFiltersOpen.set(false);
      }
    });
  }

  ngOnInit(): void {
    const marketplace = this.marketplaceSelector.marketplace;

    this.cartRefiner.selectActiveCartProducts
      .pipe(
        take(1),
        switchMap(products =>
          products.length ? this.orderDispatcher.checkCartIsValid$() : of(false),
        ),
      )
      .subscribe();

    this.shop$
      .pipe(
        takeUntil(this.destroyed$),
        skipWhile(shop => !shop),
        debounceTime(250),
        map(shop => {
          this.isOrderingAllowed = marketplace.available && !!shop?.[ShopKeys.Available];
          if (!marketplace.available) {
            this.analyticsService.trackEvent('warning', {
              warning_reason: 'marketplace_not_available',
              shop_id: shop?.[ShopKeys.Id],
              shop_name: shop?.[ShopKeys.Name],
              shop_available: shop?.[ShopKeys.Available],
              shop_available_at: shop?.[ShopKeys.AvailableAt],
              marketplace_id: marketplace[MarketplaceKeys.Id],
              marketplace_name: marketplace[MarketplaceKeys.Name],
              marketplace_available: marketplace[MarketplaceKeys.Available],
            });
          }
          if (!shop?.[ShopKeys.Available]) {
            this.analyticsService.trackEvent('warning', {
              warning_reason: 'shop_not_available',
              shop_id: shop?.[ShopKeys.Id],
              shop_name: shop?.[ShopKeys.Name],
              shop_available: shop?.[ShopKeys.Available],
              shop_available_at: shop?.[ShopKeys.AvailableAt],
              marketplace_id: marketplace[MarketplaceKeys.Id],
              marketplace_name: marketplace[MarketplaceKeys.Name],
              marketplace_available: marketplace[MarketplaceKeys.Available],
            });
          }
        }),
      )
      .subscribe();

    timer(1, 1000)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() => this.shop$),
        tap(shop => {
          if (!shop) {
            return;
          }
          if (!shop.available_at) {
            return;
          }

          const now = DateUtils.dayjsInMarketplaceTimezone();
          const dateTimeAvailableAt = DateUtils.dayjsInMarketplaceTimezone(shop.available_at);
          this.shouldBeAvailableNow =
            dateTimeAvailableAt.isBefore(now) || dateTimeAvailableAt.isSame(now);
        }),
      )
      .subscribe();

    this.shopRefiner.selectActiveFlattenProductsWithCategories
      .pipe(takeUntil(this.destroyed$))
      .subscribe(categoriesWithProducts => {
        this.updateCategoriesWithProducts({ categoriesWithProducts });
      });

    from(NativeService.isNotched())
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(isNotched => {
          this.isNotched = isNotched;
          if (this.isNotched) {
            return from(StatusBar.setStyle({ style: Style.Dark }));
          }
          return of(null);
        }),
      )
      .subscribe();

    timer(1, 1000)
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => this.updateAvailableAtMessage()),
      )
      .subscribe();

    this.getWarningMessage();
  }

  ngOnDestroy(): void {
    this.catalogFilters.clear();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  protected openCatalogFilters(): void {
    this.catalogFiltersOpen.set(true);
  }

  protected applyCatalogFilters(filters: CatalogFilterSelection[]): void {
    this.catalogFilters.apply(filters);
    this.catalogFiltersOpen.set(false);
  }

  protected removeCatalogFilter(filter: CatalogFilterSelection): void {
    const key = catalogFilterKey(filter);
    this.catalogFilters.apply(
      this.catalogFilters.applied().filter(item => catalogFilterKey(item) !== key),
    );
  }

  protected clearCatalogFilters(): void {
    this.catalogFilters.clear();
  }

  navigateTo(product: DoodProductModel, categoryId: string): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: {
          channel: 'catalog',
          reference: `category_${categoryId}`,
        },
      }),
    );
  }

  getCategoryComponentClass = (
    category: ICategory,
  ): typeof ProductCategoryCardsWithNameAtomComponent => {
    const displayTypeMap = {
      [CategoryDisplayType.CardsWithoutName]: ProductCategoryCardsWithoutNameAtomComponent,
      [CategoryDisplayType.HorizontalHighlight]: ProductCategoryHorizontalHighlightAtomComponent,
    };
    const defaultComponentClass = ProductCategoryCardsWithNameAtomComponent;

    return get(displayTypeMap, category[CategoryKeys.DisplayType], defaultComponentClass);
  };

  onTabClick({ tabId }: { tabId: string }): void {
    const tabsEl = this.categoryTabsContainer()?.nativeElement;
    if (tabsEl) {
      const topOffset = tabsEl.offsetHeight || 30;
      this.viewportScroller.setOffset([0, topOffset]);
    }
    this.viewportScroller.scrollToAnchor(tabId);
  }

  backShopListButton(url: string): void {
    this.router.navigate([this.routerHelper.translateRoute(`/${url}`)]);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    const currentScrollPosition =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    if (currentScrollPosition - this.previousScrollPosition >= 10) {
      this.isScrollingDown = true;
      return;
    }

    this.isScrollingDown = false;
    this.previousScrollPosition = currentScrollPosition;
  }

  toggleBurgerMenuNavigation(event: boolean): void {
    this.isBurgerNavigationOpen = event;
  }

  private updateAvailableAtMessage(): void {
    this.shopSelector.selectActive$
      .pipe(
        take(1),
        tap(shop => {
          if (!shop) {
            return;
          }
          this.availableAtMessage = this.shopAvailableAtService.getAvailableAtDisplayString(shop);
        }),
      )
      .subscribe();
  }

  reloadPage(): void {
    document.location.reload();
  }

  private getWarningMessage(): void {
    combineLatest([
      this.isShopLoading$,
      this.shopRefiner.selectActiveShopEnabledDistributionMode,
      this.shopSelector.selectActive$,
      this.shopSelector.selectActive$.pipe(map(shop => shop?.catalogs)),
      this.shopRefiner.selectActiveShopEnabledCatalogs,
      this.shopRefiner.selectActiveShopEnabledStores,
      this.shopRefiner.selectActiveProductsCategories,
    ])
      .pipe(takeUntil(this.destroyed$), debounceTime(50))
      .subscribe(
        ([isShopLoading, distributionModes, shop, catalogs, enabledCatalogs, stores, products]) => {
          this.warningMessages = [];

          if (isShopLoading) {
            return;
          }

          if (!distributionModes?.length) {
            this.addWarningMessage(
              'shop-page.warning-message.distribution-mode-not-available',
              shop,
            );
            return;
          }

          if (!catalogs?.length) {
            this.addWarningMessage('shop-page.warning-message.catalog-not-available', shop);
            return;
          }

          const enabledCatalogsTodayTimeRules = this.getEnabledCatalogsTodayTimeRules(
            catalogs as IShopCatalog[],
          );

          if (catalogs?.length && !enabledCatalogsTodayTimeRules?.length) {
            this.addWarningMessage('shop-page.warning-message.catalog-not-available-today', shop);
          }

          const closestFutureCatalogTimeRule = this.findClosestFutureDate(
            enabledCatalogsTodayTimeRules,
          );

          if (closestFutureCatalogTimeRule && !enabledCatalogsTodayTimeRules?.length) {
            this.addWarningMessage('shop-page.warning-message.catalog-not-available-before', shop, {
              time: dayjs()
                .startOf('day')
                .add(closestFutureCatalogTimeRule, 'second')
                .format('HH:mm'),
            });
            return;
          }

          if (enabledCatalogs?.length && !stores?.length) {
            this.addWarningMessage('shop-page.warning-message.stores-not-available', shop);
          }

          if (!products?.length) {
            this.addWarningMessage('shop-page.warning-message.products-not-available', shop);
          }
        },
      );
  }

  private addWarningMessage(
    translationKey: string,
    shop: DoodShopModel | null,
    params?: {
      [key: string]: string;
    },
  ): void {
    this.warningMessages.push(
      `${this.translateService.instant(translationKey)} ${params?.time ? params.time : ''}`,
    );

    this.analyticsService.trackEvent('warning', {
      warning_reason: 'no_product_displayed_on_shop_page',
      no_product_available_reason: translationKey.split('.').pop(),
      shop_id: shop?.[ShopKeys.Id],
      shop_name: shop?.[ShopKeys.Name],
      shop_available: shop?.[ShopKeys.Available],
      shop_available_at: shop?.[ShopKeys.AvailableAt],
    });
  }

  private getEnabledCatalogsTodayTimeRules(enabledCatalogs: IShopCatalog[] | undefined): number[] {
    return (enabledCatalogs || [])
      .reduce((acc, catalog) => {
        if (catalog?.enabled_time_rules) {
          acc.push(...catalog.enabled_time_rules);
        }
        return acc;
      }, [] as IShopCatalogTimeRule[])
      .filter(timeRule => timeRule.days_of_week.includes(DateUtils.isoDayOfWeek()))
      .map(timeRule => timeRule.start_time);
  }

  private findClosestFutureDate(dates: number[]): number | null {
    const now = dayjs();

    const dayjsDates = dates.map(seconds => dayjs().startOf('day').add(seconds, 'second'));
    const futureDates = dayjsDates.filter(date => date.isAfter(now));
    const closestFutureDate = futureDates.reduce((closestDate, currentDate) => {
      return currentDate.isBefore(closestDate) ? currentDate : closestDate;
    }, futureDates[0]);
    const closestFutureDateInSeconds = closestFutureDate?.diff(dayjs().startOf('day'), 'second');
    return closestFutureDateInSeconds || null;
  }

  private updateCategoriesWithProducts({
    categoriesWithProducts,
  }: {
    categoriesWithProducts: DoodCategoryWithProduct[];
    treeCategories?: ICategory[];
  }) {
    this.populateFiltereableLabels(categoriesWithProducts);
  }

  private populateFiltereableLabels(categoriesWithProducts: DoodCategoryWithProduct[]) {
    const filtereableLabelsMap: { [key: string]: IProductLabel } = {};

    categoriesWithProducts.forEach(category => {
      category.productsInformation?.forEach(product => {
        product[ProductKeys.Labels]?.forEach(label => {
          if (
            label.filtereable &&
            !filtereableLabelsMap[label.id] &&
            // temp fix: do not display label from DOOD marketplace or label without name
            !(
              (label.entity_owner?.entity_type === 'marketplace' &&
                label.entity_owner?.entity_id === '60ddd671c771a46420551ba4') ||
              !label.name
            )
          ) {
            filtereableLabelsMap[label.id] = label;
          }
        });
      });
    });

    this.filtereableLabels = Object.values(filtereableLabelsMap).sort(
      (a, b) => a.position - b.position,
    );
  }

  selectLabel(filtereableLabel: IProductLabel) {
    this.blockDataSource.selectLabel(filtereableLabel);
  }

  protected getProductColor = ({
    fallback,
    category,
    field,
  }: {
    fallback: string;
    category: ICategory;
    field: keyof ShopProductListBlockConfig['product_highlight_colors'];
  }): string => {
    if (category.display_type !== CategoryDisplayType.HorizontalHighlight) {
      return fallback;
    }

    const productHighlightColors = this.resolvedConfig().product_highlight_colors;
    const highlightColor = productHighlightColors?.[field];
    if (typeof highlightColor === 'string' && highlightColor) {
      return highlightColor;
    }

    // Legacy camelCase nested keys from older drafts (pre snake_case config keys).
    const camelLegacyField = String(field).replace(/_([a-z0-9])/g, (_, char: string) =>
      char.toUpperCase(),
    );
    const legacyColor = (
      productHighlightColors as Record<string, string | undefined> | undefined
    )?.[camelLegacyField];
    if (typeof legacyColor === 'string' && legacyColor) {
      return legacyColor;
    }

    return fallback;
  };

  protected getLoadingItemsArray(): number[] {
    return Array.from({ length: 20 }, (_, i) => i);
  }
}

COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopProductListBlockComponent, {});
